import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, UserPlus } from "lucide-react";

const LOGO_VERTICAL = "/logos/solara-vertical.png";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    company_name: "",
    phone: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    // APPLY-2 (2026-07-05) — match the backend rule (8+ chars, a letter and
    // a digit). The old 6-char check let clients pick a password the server
    // then rejected with a confusing error AFTER submitting the form.
    if (formData.password.length < 8 || !/[A-Za-z]/.test(formData.password) || !/\d/.test(formData.password)) {
      toast.error("Password must be at least 8 characters and include a letter and a number");
      return;
    }

    setIsLoading(true);

    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company_name: formData.company_name,
        phone: formData.phone,
      });
      toast.success(`Welcome, ${user.name}! Your account has been created.`);
      navigate("/apply");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#021d49] items-center justify-center p-12">
        <div className="text-center">
          <img 
            src={LOGO_VERTICAL} 
            alt="Solara Global" 
            className="h-64 mx-auto mb-8"
          />
          <p className="text-white/70 text-lg max-w-md">
            Create an account to start your loan application and track your progress with Solara Global Capital Partners.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#fafaf9]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <img 
              src={LOGO_VERTICAL} 
              alt="Solara Global" 
              className="h-20 mx-auto"
            />
          </div>

          <div className="bg-white p-8 rounded shadow-sm border border-gray-200">
            <h1 className="font-heading text-3xl font-bold text-[#021d49] mb-2">
              Create Account
            </h1>
            <p className="text-gray-500 mb-6">
              Register to start your loan application
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="form-label form-label-required">Full Name</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="John Smith"
                  className="form-input"
                  required
                  data-testid="register-name"
                />
              </div>

              <div>
                <Label className="form-label form-label-required">Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="your@email.com"
                  className="form-input"
                  required
                  data-testid="register-email"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Company Name</Label>
                  <Input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleChange("company_name", e.target.value)}
                    placeholder="Company Pty Ltd"
                    className="form-input"
                    data-testid="register-company"
                  />
                </div>
                <div>
                  <Label className="form-label">Phone</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="04XX XXX XXX"
                    className="form-input"
                    data-testid="register-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="form-label form-label-required">Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    placeholder="8+ characters, incl. a letter and a number"
                    className="form-input pr-10"
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="pt-1">
                {/* APPLY-3 (2026-07-08) — clearer confirm field on mobile:
                    extra spacing + explicit helper copy (MED). */}
                <Label className="form-label form-label-required">Confirm Password</Label>
                <Input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  placeholder="Re-enter the same password"
                  className="form-input"
                  required
                  data-testid="register-confirm-password"
                />
                <p className="text-xs text-gray-500 mt-1">Type the same password again so we know it's right.</p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#bd9a59] hover:bg-[#9c7e46] text-white py-3 rounded font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 mt-6"
                data-testid="register-submit"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-5 h-5" />
                    Create Account
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-500 text-sm">
                Already have an account?{" "}
                <Link to="/login" className="text-[#bd9a59] hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </div>

            <div className="mt-4 text-center">
              <Link to="/" className="text-gray-400 text-sm hover:text-gray-600">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
